import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import PipelinesPage from './pages/PipelinesPage';
import PipelineDesignerPage from './pages/PipelineDesignerPage';
import PipelineRunsPage from './pages/PipelineRunsPage';
import SchedulesPage from './pages/SchedulesPage';
import ModulesPage from './pages/ModulesPage';
import StoragePage from './pages/StoragePage';
import StorageUploadPage from './pages/StorageUploadPage';
import EndpointsPage from './pages/EndpointsPage';
import AlertsPage from './pages/AlertsPage';
import SettingsPage from './pages/SettingsPage';
import UsersPage from './pages/UsersPage';
import LogsPage from './pages/LogsPage';
import DatabaseBrowserPage from './pages/DatabaseBrowserPage';
import KnowledgeBasePage from './pages/KnowledgeBasePage';
import ProfilePage from './pages/ProfilePage';
import ApiKeysPage from './pages/ApiKeysPage';
import NotFoundPage from './pages/NotFoundPage';
import ErrorBoundary from './components/ErrorBoundary';
import { useAuth } from './store/authStore';

function App() {
  const { isAuthenticated, isLoading, checkAuth } = useAuth();
  const [appReady, setAppReady] = useState(false);
  
  // Check authentication status on app mount
  useEffect(() => {
    const initAuth = async () => {
      await checkAuth();
      setAppReady(true);
    };
    
    initAuth();
  }, [checkAuth]);
  
  // Show loading state while initializing
  if (isLoading || !appReady) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <ErrorBoundary>
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={
        !isAuthenticated
          ? <LoginPage />
          : <Navigate to="/dashboard" replace />
      } />

      {/* Protected routes */}
      <Route path="/" element={
        isAuthenticated 
          ? <Layout /> 
          : <Navigate to="/login" replace />
      }>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="pipelines" element={<PipelinesPage />} />
        <Route path="pipelines/new" element={<PipelineDesignerPage />} />
        <Route path="pipelines/:id" element={<PipelineDesignerPage />} />
        <Route path="runs" element={<PipelineRunsPage />} />
        <Route path="runs/:id" element={<PipelineRunsPage />} />
        <Route path="schedules" element={<SchedulesPage />} />
        <Route path="modules" element={<ModulesPage />} />
        <Route path="databases" element={<DatabaseBrowserPage />} />
        <Route path="alerts" element={<AlertsPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="storage">
          <Route index element={<StoragePage />} />
          <Route path="upload" element={<StorageUploadPage />} />
        </Route>
        <Route path="knowledge-base" element={<KnowledgeBasePage />} />
        <Route path="endpoints" element={<EndpointsPage />} />
        <Route path="logs" element={<LogsPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="api-keys" element={<ApiKeysPage />} />

        {/* Catch all route */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
    </ErrorBoundary>
  );
}

export default App;
